// Used in content-library-container, resources-container, and student-resources-container

// Angular
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from "@angular/core";

// Services/Types/Helpers
import { AlgoliaSearchClientHelper } from "#core/helpers";
import { LanguageHelper } from "#core/helpers/language.helper";
import { ValueList } from "#models/valueList.interface";

// Algolia

import { SearchForFacetValuesRequest } from "@algolia/client-search";
import { Asset, AssetAssociation, AuthoredBy, Edition, FilterDefinition, Level, assetTypeIds } from "@carnegie-learning/cure-types";
import { FeathersServicePaginationParameters } from "@carnegie-learning/data-access-core";
import { SearchClient } from "algoliasearch";
import { SearchOptions } from "algoliasearch-helper/types/algoliasearch";
import { AssetTypeHelper, Course } from "cli-types";

const bookTypes: Record<string, any> = {
    pe: "Student Materials",
    te: "Teacher Materials",
    explore: "iCulture",
    ancillary: "Ancillary",
    edulastic: "Edulastic",
    iculture: "iCulture",
    crv: "Close Reading Video",
    expand: "eReaders",
};

@Component({
    selector: "algolia-filters",
    templateUrl: "./algolia-filters.component.html",
    styleUrls: ["./algolia-filters.component.scss"],
    standalone: false
})
export class AlgoliaFiltersComponent implements OnChanges {
    filters: FilterDefinition[] = [];
    filterSectionLabel = {
        name: "Filter Assignables",
        fontSize: "14px",
    };
    globalResourcesCount = 0;
    typeValueList: ValueList[] = [];
    resourceValueList: ValueList[] = [];
    levelValueList: ValueList[] = [];
    selectingValueOptions: boolean;

    @Input() algoliaSearchClient: SearchClient;
    @Input() edition!: Edition;
    @Input() allEditions!: Edition[];
    @Input() subject!: AssetAssociation;
    @Input() params!: FeathersServicePaginationParameters<Asset>;
    @Input() resourcesTabActive = false;
    @Input() assignablesTabActive = true;
    @Input() assetAssociationFilters!: FilterDefinition[];
    @Input() courses!: Course[];
    @Input() isTeacher: boolean;
    @Input() isStudent: boolean;
    @Input() globalResources: Asset[] = [];
    @Input() resourceCategoriesCount: number;
    @Input() mathResourcesListView: boolean;

    @Output() emitFilterChange = new EventEmitter();
    @Output() resetFilter = new EventEmitter<boolean>();

    selectedAttibute = "";
    selectedValues: string | AuthoredBy | Level = "";
    constructor(
        private assetTypeHelper: AssetTypeHelper,
        private languageHelper: LanguageHelper,
        private algoliaSearchClientHelper: AlgoliaSearchClientHelper
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        // Clear local selection state when params are updated from parent
        if (changes.params && !changes.params.firstChange) {
            this.selectedAttibute = "";
            this.selectedValues = "";
        }
        this.globalResourcesCount = this.globalResources?.length || 0;// Update globalResourcesCount whenever globalResources input changes
        const dbFilters: Array<FilterDefinition> = [];
        if (
            this.assetAssociationFilters &&
            (changes.edition ||
                changes.assetAssociationFilters ||
                changes.assignablesTabActive)
        ) {
            this.assetAssociationFilters
                .filter((f) => {
                    if (
                        f.isDisplayedInActivities &&
                        this.assignablesTabActive
                    ) {
                        return f;
                    }
                    if (f.isDisplayedInResources && this.resourcesTabActive) {
                        return f;
                    }
                })
                .map((filterDefinition) => {
                    const facetFilterValuesPromise =
                        this.getFacetValues(filterDefinition);
                    facetFilterValuesPromise
                        .then((facetFilterValues) => {
                            if (facetFilterValues) {
                                if (
                                    filterDefinition.attribute === "assetTypeId"
                                ) {
                                    const assetActivityTypeFilter: FilterDefinition =
                                        {
                                            label: "Type", // overwritten
                                            attribute: "assetActivityTypeId",
                                        };
                                    const assetActivityTypeFilterValPromise =
                                        this.getFacetValues(
                                            assetActivityTypeFilter
                                        );
                                    return assetActivityTypeFilterValPromise.then(
                                        (assetActivityTypeFilterVal) => {
                                            if (
                                                facetFilterValues &&
                                                facetFilterValues.values
                                            ) {
                                                if (
                                                    assetActivityTypeFilterVal &&
                                                    assetActivityTypeFilterVal.values
                                                ) {
                                                    facetFilterValues = {
                                                        ...facetFilterValues,
                                                        values: [
                                                            ...facetFilterValues.values,
                                                            ...assetActivityTypeFilterVal.values,
                                                        ],
                                                    };
                                                }
                                                const excludeTypeFromFilter: Array<string> =
                                                    [
                                                        assetTypeIds.COG_TUTOR,
                                                        assetTypeIds.C2C,
                                                        assetTypeIds.EXT_LINK,
                                                    ];
                                                facetFilterValues.values =
                                                    facetFilterValues.values
                                                        ?.filter(
                                                            (type, i) =>
                                                                facetFilterValues?.values?.indexOf(
                                                                    type
                                                                ) === i &&
                                                                !excludeTypeFromFilter.includes(
                                                                    <string>type
                                                                )
                                                        )
                                                        .sort(this.optionSort);
                                                if (
                                                    this.languageHelper.ifMathLanguage(
                                                        this.edition?.subjectId
                                                    )
                                                ) {
                                                    facetFilterValues.values?.sort(
                                                        this.mathDefaultOrdering
                                                    );
                                                }
                                                return facetFilterValues;
                                            }
                                        }
                                    );
                                }
                                facetFilterValues.values =
                                    facetFilterValues.values?.sort(
                                        this.optionSort
                                    );
                                return facetFilterValues;
                            }
                        })
                        .then((facetFilterValues) => {
                            if (facetFilterValues) {
                                if (
                                    facetFilterValues.values &&
                                    facetFilterValues.label === "Type"
                                ) {
                                    const tempValueList = [];
                                    tempValueList.push({
                                        value: "Type",
                                        id: "noTypeSelected",
                                    });
                                    facetFilterValues.values.forEach(
                                        (v, index) => {
                                            const value = {
                                                value: v,
                                                id: index,
                                            };
                                            tempValueList.push(value);
                                        }
                                    );
                                    this.typeValueList = tempValueList;
                                } else if (
                                    facetFilterValues.values &&
                                    facetFilterValues.label === "Resource"
                                ) {
                                    const tempValueList = [];
                                    tempValueList.push({
                                        value: "Resource",
                                        id: "noResourceSelected",
                                    });
                                    facetFilterValues.values.forEach(
                                        (v, index) => {
                                            const value = {
                                                value: v,
                                                id: index,
                                            };
                                            tempValueList.push(value);
                                        }
                                    );
                                    this.resourceValueList = tempValueList;
                                } else if (
                                    facetFilterValues.values &&
                                    facetFilterValues.label === "Level"
                                ) {
                                    const tempValueList = [];
                                    tempValueList.push({
                                        value: "Level",
                                        id: "noResourceSelected",
                                    });
                                    facetFilterValues.values.forEach(
                                        (v, index) => {
                                            const value = {
                                                value: v,
                                                id: index,
                                            };
                                            tempValueList.push(value);
                                        }
                                    );
                                    this.levelValueList = tempValueList;
                                }
                                dbFilters.push(facetFilterValues);
                                // PAS-7974: keep filters in order defined in DB
                                this.filters = dbFilters.sort(
                                    (a, b) =>
                                        this.assetAssociationFilters.findIndex(
                                            (i) => a.attribute === i.attribute
                                        ) -
                                        this.assetAssociationFilters.findIndex(
                                            (i) => b.attribute === i.attribute
                                        )
                                );
                                if (this.assignablesTabActive) {
                                    this.addDefaultFilters();
                                }
                            }
                        });
                });
        }
        if (changes.courses && this.assignablesTabActive) {
            this.addDefaultFilters();
        }
    }

    updateFilterValues(
        attribute: string,
        value: string | AuthoredBy | Level
    ): void {
        this.selectedAttibute = attribute;
        this.selectedValues = value;
    }

    updateFilterValuesStudent(
        attribute: string,
        value: string | AuthoredBy | Level
    ): void {
        this.selectedAttibute = attribute;
        this.selectedValues = value;
    }

    saveMultipleSelections(event: ValueList[]) {
        // const secondaryTeacherIDs = Array();
        // const secondaryTeacherIDsForForm = Array();
        // event.forEach((x: ValueList) => {
        //     const id = x.id;
        //     secondaryTeacherIDs.push({ selectedId: id });
        //     secondaryTeacherIDsForForm.push(id);
        // });
        // this.formSecondaryTeacherIDs = secondaryTeacherIDs;
        // this.secondaryTeacherValueList.forEach((teacher) => {
        //     const tFound = secondaryTeacherIDsForForm.find(
        //         (y) => y === teacher.id
        //     );
        //     teacher.checked = !!tFound;
        // });
        // let patchValue = secondaryTeacherIDsForForm;
        // if (secondaryTeacherIDsForForm[0] === "noSecondaryTeacherSelected") {
        //     patchValue = [];
        // }
        // this.courseForm.patchValue({
        //     secondaryTeacherIDs: patchValue,
        // });
    }

    optionSort(value1: any, value2: any) {
        let result: number;
        const justStrings: boolean =
            typeof value1 === "string" && typeof value2 === "string";
        const numberStrings: boolean = !!(
            value1 &&
            !isNaN(parseFloat(value1.toString())) &&
            value2 &&
            !isNaN(parseFloat(value2.toString()))
        );
        let num1: number;
        let num2: number;
        if (justStrings) {
            if (numberStrings) {
                num1 = parseFloat(value1.toString());
                num2 = parseFloat(value2.toString());
                if (num1 < num2) {
                    result = -1;
                } else if (num1 > num2) {
                    result = 1;
                } else {
                    result = 0;
                }
            } else {
                if (value1 < value2) {
                    result = -1;
                } else if (value1 > value2) {
                    result = 1;
                } else {
                    result = 0;
                }
            }
            return result;
        } else {
            return 0; // if objects are passed in, just let them appear in the order they were sent over.
        }
    }
    mathDefaultOrdering(a: any, b: any) {
        const defaultMathOrder = [
            "MATHBOOK",
            "MATHIA-UNIT",
            "NON-MASTERY",
            "MASTERY",
            "MIXED-PRACTICE",
            "READYCHECK",
            "BUNDLE",
            "CUSTOM-ACT",
        ];
        return (
            defaultMathOrder.indexOf(<string>a) -
            defaultMathOrder.indexOf(<string>b)
        );
    }

    emitFilterValues(event: Boolean): void {
        if (!event) {
            // false means close selection dropdown
            this.emitFilterChange.emit({
                [this.selectedAttibute]: this.selectedValues,
            });
        } else {
            // event true means open a filter dropdown then we should clear out the previous selected values
            this.selectedAttibute = "";
            this.selectedValues = "";
        }
    }
    getSelected(attribute: string) {
        // Check if we have a pending selection for this attribute (optimistic UI)
        if (this.selectedAttibute === attribute && this.selectedValues !== "") {
            if (attribute === "levelIds") {
                return Array.isArray(this.selectedValues) ? this.selectedValues : [this.selectedValues];
            }
            return this.selectedValues;
        }
        
        // Fall back to params state for persisted selections
        if (this.params && this.params.filter.hasOwnProperty(attribute)) {
            // Ensure that all levelIds are converted into an array
            if(attribute === "levelIds"){
                const presetFilterLevelId = this.params.filter[attribute];
                if (presetFilterLevelId === undefined || presetFilterLevelId === null) {
                    return [];
                }
                return Array.isArray(presetFilterLevelId) ? presetFilterLevelId : [presetFilterLevelId];
            }
            return (<any>this.params.filter)[attribute]; // TODO: something better?
        }
        
        // Return empty array for multiple select attributes when no filter is set
        if (attribute === "levelIds") {
            return [];
        }
        
        return undefined;
    }

    getValue(attribute: string, value: string | AuthoredBy | Level) {
        if (attribute === "levelIds") {
            return (<Level>value).levelId;
        } else if (attribute === "bookType") {
            return Object.keys(bookTypes).find(
                (key) => bookTypes[key] === value
            );
        } else if (typeof value === "string") {
            return value;
        } else {
            return Object.keys(value)[0];
        }
    }

    getCssClass(): string {
        let returnString = this.languageHelper.ifMathLanguage(
            this.edition?.subjectId
        )
            ? this.edition?.subjectId.toLowerCase()
            : "non-math";
        returnString += " ";
        const additionClassName: { [index: number]: string } = {
            3: "thirds",
            4: "quarters",
            5: "fifths",
            6: "sixths",
            7: "sevenths",
            8: "eigths",
            9: "ninths",
            10: "tenthsOrMore",
        };
        if (this.filters && this.filters.length) {
            Object.keys(additionClassName).forEach((key) => {
                const keyNum: number = parseInt(key, 10);
                if (keyNum === this.filters.length) {
                    returnString += additionClassName[keyNum];
                }
            });
            if (this.filters.length < 3) {
                returnString += "thirds";
            } else if (this.filters.length > 10) {
                returnString += "tenthsOrMore";
            }
        }
        returnString += this.isTeacher ? " teacher" : " student";
        returnString += this.assignablesTabActive
            ? " assignments"
            : " resources";
        return returnString;
    }

    getDisplayName(key: string, value: string | AuthoredBy | Level | boolean) {
        if (key === "assetTypeId" || key === "resourceType") {
            return this.assetTypeHelper.getCapTypeName(<string>value);
        } else if (key === "levelIds") {
            return (<Level>value).levelName;
        } else if (key === "visibility" || key === "courseID") {
            return (<any>value)[Object.keys(value)[0]]; // TODO: Fix this better. Not even sure what's going on here.
        } else if (key === "autoEvaluate") {
            // Adding 'true' incase we missed some data in CURE-493
            return ["true", true].includes(value as string | boolean)
                ? "Yes"
                : "No";
        } else {
            return value;
        }
    }

    getIconPath(shortAssignableType: string) {
        return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
            shortAssignableType
        );
    }

    async getFacetValues(
        filterDefinition: FilterDefinition
    ): Promise<FilterDefinition | null> {
        let filters = "";

        /* searchFacetParams filter should be generic and not include filter selection. i.e. assetType, units, lessons
    and it should include editionIds, isAssignable and isResource */
        const filteredParams = this.algoliaSearchClientHelper.getFilteredParams(
            this.params
        );
        if (this.edition && filteredParams) {
            filters = this.algoliaSearchClientHelper.createAlgoliaFilter(
                filteredParams,
                this.resourcesTabActive,
                this.isTeacher,
                this.edition
            );
        }
        const searchFacetParams: SearchForFacetValuesRequest &
            SearchOptions = {
            facetQuery: "",
            filters,
        };
        return await this.algoliaSearchClient
            .searchForFacetValues(
                {
                    indexName: this.algoliaSearchClientHelper.getIndexName(),
                    facetName: filterDefinition.attribute,
                    searchForFacetValuesRequest: searchFacetParams,
                },
            )
            .then((facetSearchResults) => {
                let filterValues: string[] | Level[] =
                    facetSearchResults.facetHits
                    .map((hit) => hit.value);
                if (
                    filterDefinition.attribute === "levelIds" &&
                    this.edition?.levelIds &&
                    this.subject?.levels
                ) {
                    filterValues = this.subject.levels.filter((l) =>
                        this.edition.levelIds?.includes(l.levelId)
                    );
                }
                if (
                    filterDefinition.attribute === "levelIds" &&
                    this.edition?.levelIds &&
                    this.subject?.levels &&
                    this.allEditions &&
                    this.edition.supplementEditionId
                ) {
                    const supplementEdition: Edition | undefined =
                        this.allEditions.find(
                            (e) =>
                                e.editionId === this.edition.supplementEditionId
                        );
                    if (supplementEdition) {
                        const supplementValues = this.subject.levels.filter(
                            (l) =>
                                supplementEdition?.levelIds?.includes(l.levelId)
                        );
                        // merging edition filter value into supplement filter value
                        (<Level[]>filterValues).forEach((v) => {
                            if (
                                !supplementValues.some(
                                    (value) => value.levelId === v.levelId
                                )
                            ) {
                                supplementValues.push(v);
                            }
                        });
                        filterValues = supplementValues;
                    }
                }
                if (filterDefinition.attribute === "bookType") {
                    if (this.resourcesTabActive) {
                        return null;
                    }
                    filterValues = (<string[]>filterValues)
                        .map((val: string) => bookTypes[val])
                        .filter((attr) => attr !== undefined);
                }
                // Removes iCulture from appearing in the Resource filter on Resources tab
                if(filterDefinition.attribute === "bookSubType" && this.resourcesTabActive){
                    const potentiallySuppressableGlobalResourceFilterOptions = ['edulastic','help center','iculture'],
                          suppressedGlobalResourceFilterOptions: string[] = (!!this.globalResources.length) ? potentiallySuppressableGlobalResourceFilterOptions.filter(option => !!this.globalResources.map(gr => gr.displayName.toLowerCase()).find(dName => dName.includes(option))) : [];
                    filterValues = (<string[]>filterValues).filter(val => !suppressedGlobalResourceFilterOptions.includes(val.toLowerCase()));
                }
                // Only show filters with values
                if (filterValues && filterValues.length > 0) {
                    return <FilterDefinition>{
                        attribute: filterDefinition.attribute,
                        label: filterDefinition.label
                            ? filterDefinition.label
                            : filterDefinition.attribute,
                        values: filterValues,
                    };
                }
                return null;
            });
    }

    addDefaultFilters() {
        const isMathProgram = this.languageHelper.ifMathLanguage(
            this.edition?.subjectId
        );
        let global = "Carnegie Learning";
        if (!isMathProgram && this.edition) {
            global = this.edition.editionName + " Content";
        }
        if (
            this.isTeacher &&
            this.filters.length &&
            this.assignablesTabActive
        ) {
            let DefaultFilters: FilterDefinition[] = [
                {
                    attribute: "visibility",
                    label: "Authored By",
                    values: [
                        { self: "Educator Created" },
                        { school: "Teachers in My School" },
                        { district: "Teachers in My District" },
                        { none: "Passport Community" },
                        { global },
                    ],
                },
            ];
            
            // If the editionId matches the first course in the list, add the Assigned in Class filter, 
            // if there is no courses in the list, just continue with just the Authored By filter
            const firstCourse = this.courses[0]?.editionId;

            if (this.edition.editionId === firstCourse) {
                const courseFilterValues = this.courses.map((course) => {
                    return {
                        [course.courseID]: course.courseTitle,
                    };
                });
                DefaultFilters = [
                    ...DefaultFilters,
                    {
                        attribute: "courseID",
                        label: "Assigned In Class",
                        values: [...courseFilterValues],
                    },
                ];
            } 
            
            const defaultAttrs = DefaultFilters.map((f) => f.attribute);

            // We need to ensure there are no default filters before attaching, prevent previous selected edition courseId filter staying in the filters.
            this.filters = this.filters.filter(
                (f) => !defaultAttrs.includes(f.attribute)
            );
            // attaching default filters at the end of the filters
            this.filters = [...this.filters, ...DefaultFilters];
        }
    }

    onReset() {
        this.resetFilter.emit(true);
    }
}
